import { ChangeDetectionStrategy, Component, ElementRef, ViewChild, OnDestroy } from '@angular/core'
import { UntypedFormControl } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { combineLatest, ReplaySubject, Subscription, Subject } from 'rxjs'
import { distinctUntilChanged, filter, flatMap, map, shareReplay, take, tap, takeUntil } from 'rxjs/operators'
import { UrlService } from '../singleton-services/url.service'
import { AuthService } from '../singleton-services/auth/auth.service'
import { VerticalService } from '../singleton-services/vertical.service'
import { NodeService } from '../shared/services/node.service'
import { SearchService } from '../singleton-services/search.service'
import { SegmentService } from '../shared/analytics/services/segment.service'
import { SegmentTagMap } from '../shared/analytics/models/segment-tag-map.model'
import { VerticalSearchComponent } from '../shared/vertical-search/vertical-search.component'
import { FloApiResponse, Meta } from '../shared/models/api-response.model'
import { Video } from '../shared/models/video.model'
import { IVideo } from '../shared/services/video.service'
import { mapFacetUrlParamsToInterface } from '../shared/models/facet.interface'
import { ISearchQueryParams } from '../shared/models/search-query-params.interface'
import {
  DROPDOWN_NAME_TYPE,
  DROPDOWN_OPTION_TYPE,
  SegmentDropdownOptionSelected,
  SegmentEvents
} from '../shared/analytics/models/segment-events.model'
import { VerticalSearchService } from '../singleton-services/vertical-search.service'

interface VideosParams {
  q?: string
  facets?: any
  sort?: string
  playing?: string
  limit?: number
}

@Component({
  selector: 'flo-training',
  templateUrl: './training.component.html',
  styleUrls: ['./training.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrainingComponent implements OnDestroy {
  @ViewChild('videoPlayer', { read: ElementRef })
  set videoPlayerContainer(videoPlayerContainer: ElementRef<HTMLElement>) {
    if (!!videoPlayerContainer?.nativeElement) {
      this.videoPlayer$.next(videoPlayerContainer.nativeElement)
    }
  }

  constructor(
    private ar: ActivatedRoute,
    private url: UrlService,
    private router: Router,
    private auth: AuthService,
    private vs: VerticalService,
    private ns: NodeService,
    private segmentService: SegmentService,
    private searchService: SearchService,
    private verticalSearch: VerticalSearchService
  ) {}

  PAGE_LIMIT = 15
  private readonly videoPlayer$ = new ReplaySubject<HTMLElement>(1)
  public readonly emitThumbnailClick$ = new ReplaySubject<boolean>(1)

  @ViewChild(VerticalSearchComponent)
  vertSearch: VerticalSearchComponent

  urlParams$ = this.ar.queryParams.pipe(
    distinctUntilChanged(),
    map(queryParams => this.verticalSearch.parseFacets(queryParams)),
    map(a =>
      Object.keys(a).reduce((acc, c) => ({ ...acc, [c]: this.url.decode(a[c]) }), {} as { [key: string]: string })
    ),
    shareReplay(1)
  )

  videosResponse$ = this.ar.queryParams.pipe(
    distinctUntilChanged(),
    map(videosParams => this.verticalSearch.parseFacets(videosParams)),
    map(videosParams => mapVideosParamsToISearchQueryParams(videosParams)),
    map(searchQueryParams => ({
      sort: 'recent' as 'recent',
      ...searchQueryParams,
      category: 'Training',
      type: 'video' as 'video'
    })),
    flatMap(
      params => this.searchService.search<ReadonlyArray<IVideo>>(params),
      (params, response) => ({ params, response })
    ),
    shareReplay(1)
  )

  view$ = combineLatest([this.videosResponse$, this.auth.user$, this.vs.siteSettings$]).pipe(
    map(([videosResponse, user, settings]) => {
      const params = videosResponse.params
      const extractedVid = this.extractPlayingVideoFromMeta(videosResponse.response)
      const meta = videosResponse.response.meta
      const videos = videosResponse.response.data || []
      const canLoadMore = meta && meta.page && meta.pages && meta.page < meta.pages
      const nextLimit = ((params.limit && +params.limit) || this.PAGE_LIMIT) + this.PAGE_LIMIT
      const playingVideo = videos.find(a => a && a.id === +(params.include_node || 0)) || extractedVid
      const nextVideoIndex = videos.findIndex(a => a && a.id === +(params.include_node || 0))
      const nextVideoId =
        (videos[nextVideoIndex] && videos[nextVideoIndex + 1] && videos[nextVideoIndex + 1].id) ||
        (videos[0] && videos[0].id)
      const searchTerm = this.vertSearch ? this.vertSearch.searchTerm : params.q || ''

      const selectedVideo = playingVideo && {
        player: this.videoPlayerExtractor(playingVideo, settings),
        info: this.getPlayingVideoInfo(playingVideo)
      }

      return {
        isLoggedInUser: user.loggedIn,
        isAuthorizedUser: user.isPremium,
        selectedVideo,
        params,
        videos,
        queryParamsFn: (video: IVideo) => ({ playing: video.id }),
        searchTerm,
        sort: params.sort || 'recent',
        searchTitle: `Find ${settings.training_videos_name || 'Training'} Videos`,
        totalTitle: 'Video',
        totalTitlePlural: 'Videos',
        channel: 'techniques-page',
        canLoadMore,
        topSortControl: new UntypedFormControl(params.sort, []),
        nextVideoId,
        nextLimit,
        notFoundWarning: meta.error ? "Sorry, we don't have any results for that." : undefined,
        selectOptions: {
          recent: 'Most Recent',
          view_count: 'Most Watched'
        }
      }
    }),
    tap(view => {
      view.topSortControl.valueChanges.pipe(take(1)).subscribe(sort => {
        this.trackSelectOptions(sort)
        this.searchWithoutVideo({ sort })
      })
    }),
    shareReplay(1)
  )

  public currentFacets$ = this.videosResponse$.pipe(
    map(videosResponse => videosResponse.response.meta.extra),
    filter<readonly any[]>(Boolean),
    map(extra => extra[0]),
    filter<any>(Boolean),
    map(zero => ({
      videoFilters: zero.video_filters,
      displayNames: zero.facet_display_names
    })),
    filter(({ videoFilters }) => !!videoFilters),
    map(({ videoFilters, displayNames }) => this.verticalSearch.buildFacetsObject(videoFilters, displayNames))
  )

  public allFacets$ = this.searchService.getFacets<ReadonlyArray<IVideo>>({ category: 'Training', type: 'video' }).pipe(
    take(1),
    map(response => response.meta.extra),
    filter<readonly any[]>(Boolean),
    map(extra => extra[0]),
    filter<any>(Boolean),
    map(zero => ({
      videoFilters: zero.video_filters,
      displayNames: zero.facet_display_names
    })),
    filter(({ videoFilters }) => !!videoFilters),
    map(({ videoFilters, displayNames }) => this.verticalSearch.buildFacetsObject(videoFilters, displayNames))
  )

  public resultsCount$ = this.videosResponse$.pipe(
    map(videosResponse => videosResponse.response),
    filter<FloApiResponse<readonly IVideo[]>>(Boolean),
    map(response => response.meta),
    filter<Meta>(Boolean),
    map(meta => meta.total)
  )

  public selectedFacets$ = this.urlParams$.pipe(
    map(params => params && params.facets),
    filter(Boolean),
    map((urlFacets: any) => mapFacetUrlParamsToInterface(urlFacets)),
    shareReplay(1)
  )

  private readonly ngOnDestroy$ = new Subject<void>()

  public scrollToVideoPlayer$ = combineLatest([
    this.emitThumbnailClick$,
    this.videoPlayer$.pipe(distinctUntilChanged())
  ])
    .pipe(takeUntil(this.ngOnDestroy$))
    .subscribe(([thumbnailClick, videoPlayer]) => {
      videoPlayer.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      })
    })

  public updateMetaTagsBySelectedVideo: Subscription = this.view$
    .pipe(
      map((view: any) => view.selectedVideo),
      filter(Boolean),
      map((selectedVideo: any) => selectedVideo.player),
      filter(Boolean)
    )
    .subscribe((video: Video) => this.ns.applyNodeMetaToPage(video))

  public trackSelectedVideo: Subscription = this.view$
    .pipe(
      map((view: any) => view.selectedVideo && view.selectedVideo.player),
      distinctUntilChanged((prev, curr) => (prev && prev.id) === (curr && curr.id)),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe((video: Video) =>
      this.segmentService.page(
        undefined,
        `${video ? SegmentTagMap.TRAINING_VIDEO.name : SegmentTagMap.TRAINING_HOME.name}`,
        undefined,
        video
      )
    )

  extractPlayingVideoFromMeta(response: FloApiResponse<ReadonlyArray<IVideo>, Meta & { extra?: ReadonlyArray<any> }>) {
    return (
      response.meta &&
      response.meta.extra &&
      response.meta.extra.reduce(
        (acc, cur) =>
          Object.keys(cur)
            .filter(a => a === 'include_node')
            .map(a => (cur as { [key: string]: any })[a] as string)
            .pop(),
        {}
      )
    )
  }

  getPlayingVideoInfo(playingVideo: any | undefined) {
    const _playingVideo = playingVideo || {}
    return {
      title: _playingVideo.title,
      date: _playingVideo.publish_start_date,
      description: _playingVideo.description || _playingVideo.description_plain,
      isRich: _playingVideo.description ? true : false
    }
  }

  videoPlayerExtractor(_video: any, siteSettings: any) {
    const video = _video || {}
    return {
      ...video,
      imgUrl: video.asset && video.asset.url,
      nodeId: video.id,
      assocId:
        video.node &&
        video.node.primary_event_or_series_association &&
        video.node.primary_event_or_series_association.id,
      adsetId: siteSettings.ooyala_master_ad_set,
      playerId: siteSettings.ooyala_player_id,
      playerColor: siteSettings.primary_color,
      contentId: video.content_id,
      title: video.title,
      routerLink: ['../', `${video.id}-${video.slug}`]
    }
  }

  onVideoSelectionChange(e: IVideo) {
    this.router.navigate([], {
      queryParams: { playing: e.id },
      queryParamsHandling: 'merge'
    })
  }

  onVertSearchChange(qp: any) {
    this.searchWithoutVideo(qp)
  }

  searchWithoutVideo(qp: any) {
    const { playing, ...queryParams } = this.ar.snapshot.queryParams
    const params = {
      ...queryParams,
      ...qp
    }
    params.facets = qp.facets && Object.keys(qp.facets).length ? JSON.stringify(qp.facets) : undefined
    this.router.navigate([], {
      queryParams: params,
      queryParamsHandling: 'merge'
    })
  }

  onPage(limit: number) {
    this.router.navigate([], {
      queryParams: { limit },
      queryParamsHandling: 'merge'
    })
  }

  onPlayed(id: any) {
    if (id) {
      this.onVideoSelectionChange({ id } as any)
    }
  }
  ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }

  private trackSelectOptions(sort: string) {
    const dropdownEvent: SegmentDropdownOptionSelected = {
      name: SegmentEvents.DROPDOWN_OPTION_SELECTED,
      properties: {
        name: DROPDOWN_NAME_TYPE.SORT_BY,
        sort_by: sort === 'recent' ? DROPDOWN_OPTION_TYPE.MOST_RECENT : DROPDOWN_OPTION_TYPE.MOST_WATCHED,
        node: undefined
      }
    }
    this.segmentService.trackEvent(dropdownEvent)
  }
}

function mapVideosParamsToISearchQueryParams(videosParams: VideosParams): ISearchQueryParams {
  const searchQueryParams: ISearchQueryParams = {
    q: videosParams.q,
    sort: videosParams.sort as any,
    include_node: parseInt(videosParams.playing as string, 10),
    limit: videosParams.limit,
    type: 'video',
    facets: videosParams.facets
  }
  const definedParams = Object.keys(searchQueryParams)
    .filter(key => !!searchQueryParams[key])
    .reduce(
      (params: { [key: string]: any }, key) => ({
        ...params,
        [key]: searchQueryParams[key]
      }),
      {}
    )
  return definedParams
}

interface VideosParams {
  q?: string
  facets?: any
  sort?: string
  playing?: string
  limit?: number
}
